import { FC, Fragment, useEffect, useMemo, useRef, useState } from "react";
import { useSession } from "next-auth/react";
import { UserPublic } from "@/app/_lib/types/user.types";
import { getUserNameDisplay } from "@/app/_lib/utils/user.utils";

type Props = {
  label: string;
  name: string;
  usersPublic: Record<string, UserPublic>;
  required?: boolean;
  placeholder?: string;
  defaultValue?: string;
  value?: string;
  error?: string;
  onChange?: (value: string) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  onMentionedUsersChange?: (users: UserPublic[]) => void;
};

const TextareaWithMentions: FC<Props> = ({
  label,
  name,
  usersPublic,
  required,
  placeholder,
  defaultValue,
  value,
  error,
  onChange,
  onKeyDown,
  onMentionedUsersChange,
}) => {
  const { data: session } = useSession();
  const userId = session?.user?.id;

  const [mentionedUsers, setMentionedUsers] = useState<UserPublic[]>([]);
  const [showUserList, setShowUserList] = useState(false);
  const [userFilter, setUserFilter] = useState("");
  const [cursorPosition, setCursorPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const [selectedUserIndex, setSelectedUserIndex] = useState(0);

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const userListRef = useRef<HTMLDivElement>(null);

  /**
   * Filter the users based on the user filter text
   * Exclude the current user from the list
   */
  const filteredUsers = useMemo(() => {
    return Object.values(usersPublic).filter(
      (user) =>
        user.id !== userId &&
        (!userFilter
          ? true
          : getUserNameDisplay(user)
              .toLowerCase()
              .includes(userFilter.toLowerCase()))
    );
  }, [usersPublic, userFilter, userId]);

  useEffect(() => {
    onMentionedUsersChange?.(mentionedUsers);
  }, [mentionedUsers]);

  /**
   * Handle keyboard events for the textarea, including:
   * - Arrow keys and Tab for navigating through user mention suggestions
   * - Enter/Space to select a user mention
   * - Escape to close the mention suggestions
   * @param e The keyboard event from the textarea
   */
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    onKeyDown?.(e);

    // Only handle keyboard events if the user list popup is shown
    if (showUserList) {
      // Handle navigation through user list with Tab and arrow keys
      if (e.key === "Tab" || e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault(); // Prevent default behavior of these keys
        setSelectedUserIndex((prev) => {
          // For ArrowUp, move selection up or wrap to bottom
          if (e.key === "ArrowUp") {
            return prev > 0 ? prev - 1 : filteredUsers.length - 1;
          }
          // For Tab/ArrowDown, move selection down or wrap to top
          return prev < filteredUsers.length - 1 ? prev + 1 : 0;
        });
      }
      // Handle selecting a user with Enter or Space
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault(); // Prevent newline or space insertion
        insertUserMention(filteredUsers[selectedUserIndex]); // Insert the selected user mention
      }
      // Handle closing the user list with Escape
      if (e.key === "Escape") {
        setShowUserList(false); // Hide the user list popup
      }
    }

    // Handle backspace to delete entire mention
    if (e.key === "Backspace" && textareaRef.current) {
      const cursorPos = textareaRef.current.selectionStart;
      const text = textareaRef.current.value;

      // Look for @ symbol before cursor
      const textBeforeCursor = text.slice(0, cursorPos);
      const lastAtPos = textBeforeCursor.lastIndexOf("@");

      if (lastAtPos >= 0) {
        // Get the potential mention text
        const potentialMention = text.slice(lastAtPos, cursorPos);

        // Check if this matches any mentioned users
        const matchedUser = Object.values(usersPublic).find(
          (user) => `@${getUserNameDisplay(user)}` === potentialMention
        );

        if (matchedUser) {
          e.preventDefault();
          // Remove the entire mention
          const newValue = text.slice(0, lastAtPos) + text.slice(cursorPos);
          onChange?.(newValue);

          // Update mentioned users
          setMentionedUsers((prev) =>
            prev.filter((u) => u.id !== matchedUser.id)
          );

          // Set cursor position
          setTimeout(() => {
            if (textareaRef.current) {
              textareaRef.current.setSelectionRange(lastAtPos, lastAtPos);
            }
          }, 0);
        }
      }
    }
  };

  /**
   * Handle changes to the textarea value
   * @param e The change event from the textarea
   */
  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    // Get the new value from the textarea and call onChange handler if provided
    const newValue = e.target.value;
    onChange?.(newValue);

    // Get the current cursor position in the textarea
    const cursorPos = e.target.selectionStart;
    // Get all text before the cursor position
    const textBeforeCursor = newValue.slice(0, cursorPos);
    // Find the position of the last @ symbol before cursor
    const lastAtSymbol = textBeforeCursor.lastIndexOf("@");

    // If no @ symbol found, hide user list and exit
    if (lastAtSymbol === -1) {
      setShowUserList(false);
      return;
    }

    // Check if there's whitespace before the @ symbol
    const charBeforeAt =
      lastAtSymbol > 0 ? textBeforeCursor[lastAtSymbol - 1] : "";
    // If @ is not at start and no whitespace before it, hide list and exit
    if (!charBeforeAt.match(/\s/) && lastAtSymbol !== 0) {
      setShowUserList(false);
      return;
    }

    // Get the text between @ and cursor
    const textAfterAt = textBeforeCursor.slice(lastAtSymbol + 1);

    if (textAfterAt.length === 0) {
      // When @ is just typed, show the popup at cursor position
      const coords = getCaretCoordinates(e.target, cursorPos);
      setCursorPosition(coords);
      setShowUserList(true);
      // Reset filter and selection
      setUserFilter("");
      setSelectedUserIndex(0);
    } else if (/^[a-zA-Z0-9]*$/.test(textAfterAt)) {
      // If typing alphanumeric chars after @, update filter
      setUserFilter(textAfterAt);
      setShowUserList(true);
    } else {
      // If typing any other character, hide the popup
      setShowUserList(false);
    }
  };

  /**
   * Calculate the coordinates of the caret (cursor) position in a textarea
   * This is used to position the user mention popup at the correct location
   *
   * @param element - The textarea element to get coordinates from
   * @param position - The cursor position in the textarea text
   * @returns Coordinates object with top and left positions
   */
  const getCaretCoordinates = (
    element: HTMLTextAreaElement,
    position: number
  ) => {
    // Get the textarea's bounding rectangle
    const rect = element.getBoundingClientRect();

    // Create a hidden div to measure text dimensions
    const div = document.createElement("div");
    const styles = window.getComputedStyle(element);

    // Copy all relevant styles from textarea to div
    const stylesToCopy = [
      "fontFamily",
      "fontSize",
      "fontWeight",
      "lineHeight",
      "paddingTop",
      "paddingRight",
      "paddingBottom",
      "paddingLeft",
      "borderWidth",
      "boxSizing",
      "letterSpacing",
      "wordSpacing",
      "whiteSpace",
      "wordBreak",
      "overflowWrap",
    ];

    stylesToCopy.forEach((style) => {
      div.style[style as any] = styles[style as any];
    });

    // Set div width to match textarea's content width
    div.style.width = `${
      element.clientWidth -
      parseInt(styles.paddingLeft) -
      parseInt(styles.paddingRight)
    }px`;

    // Set div content to text up to cursor position
    div.textContent = element.value.slice(0, position);

    // Add a span at the end to measure cursor position
    const span = document.createElement("span");
    span.textContent = ".";
    div.appendChild(span);

    // Style the div to be invisible but maintain text layout
    div.style.position = "absolute";
    div.style.visibility = "hidden";
    div.style.whiteSpace = "pre-wrap";

    // Add div to DOM temporarily to measure dimensions
    document.body.appendChild(div);

    // Get the span's position (where our cursor is)
    const spanRect = span.getBoundingClientRect();
    const divRect = div.getBoundingClientRect();

    // Clean up by removing the temporary div
    document.body.removeChild(div);

    // Calculate coordinates relative to the viewport
    const top = rect.top + spanRect.top - divRect.top - element.scrollTop;
    const left = rect.left + spanRect.left - divRect.left - element.scrollLeft;

    return {
      top: top + spanRect.height, // Position below the current line
      left: left,
    };
  };

  /**
   * Insert a user mention into the comment text when a user is selected from the mention list
   * @param user - The user to insert into the comment text
   */
  const insertUserMention = (user: UserPublic) => {
    // Add the user to the mentioned users list if they aren't already in there
    if (!mentionedUsers.some((u) => u.id === user.id)) {
      setMentionedUsers([...mentionedUsers, user]);
    }

    // Get the current cursor position in the textarea, defaulting to 0 if not available
    const cursorPos = textareaRef.current?.selectionStart || 0;
    // Get all text before the cursor position
    const textBeforeCursor = value?.slice(0, cursorPos) || "";
    // Get all text after the cursor position
    const textAfterCursor = value?.slice(cursorPos) || "";
    // Find the position of the last @ symbol in the text before cursor
    const lastAtSymbol = textBeforeCursor.lastIndexOf("@");
    // Get all text before the @ symbol
    const textBeforeAt = textBeforeCursor.slice(0, lastAtSymbol);
    // Get any non-space characters immediately after the cursor position
    const textAfterAtUntilSpace = textAfterCursor.match(/^[^\s]*/)?.[0] || "";
    // Get the remaining text after any non-space characters
    const remainingText = textAfterCursor.slice(textAfterAtUntilSpace.length);

    // Construct the new value by combining:
    // 1. Text before the @ symbol
    // 2. The @ symbol plus the user's display name and a space
    // 3. The remaining text after any partial mention
    const newValue =
      textBeforeAt + `@${getUserNameDisplay(user)} ` + remainingText;
    // Update the value state with the new text
    onChange?.(newValue);
    // Hide the user mention list
    setShowUserList(false);

    // Calculate where to place the cursor after inserting the mention
    const newCursorPos = lastAtSymbol + getUserNameDisplay(user).length + 2; // +2 for @ and space

    // Use setTimeout to ensure the textarea has updated before setting focus and cursor
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(newCursorPos, newCursorPos);
      }
    }, 0);
  };

  return (
    <Fragment>
      <label className="form-control w-full">
        {!!label && (
          <div className="label">
            <span className="label-text">{label}</span>
          </div>
        )}
        <textarea
          onChange={handleChange}
          name={name}
          className="textarea h-24"
          required={required}
          placeholder={placeholder}
          defaultValue={defaultValue}
          value={value}
          onKeyDown={handleKeyDown}
          ref={textareaRef}
        />
        <div className="label">
          {error && <span className="label-text-alt text-error">{error}</span>}
        </div>
      </label>
      {showUserList && cursorPosition && (
        <div
          ref={userListRef}
          className="fixed z-50 bg-base-200 rounded-md max-h-48 overflow-y-auto w-64"
          style={{
            top: `${cursorPosition.top + 10}px`,
            left: `${cursorPosition.left - 15}px`,
          }}
        >
          {filteredUsers.map((user, index) => (
            <div
              key={user.id}
              className={`p-2 hover:bg-base-300 cursor-pointer ${
                index === selectedUserIndex ? "bg-base-300" : ""
              }`}
              onClick={() => insertUserMention(user)}
              onMouseEnter={() => setSelectedUserIndex(index)}
            >
              <div className="flex items-center gap-2">
                <span className="inline-block">{getUserNameDisplay(user)}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </Fragment>
  );
};

export default TextareaWithMentions;
